import React, {useEffect, useState} from 'react';
import BrowseToolbar from '@/components/football/BrowseToolbar';
import Input from '@/components/ui/Input';
import {PlayerBrowseFacets} from '@/features/players/api';
import {parsePlayerAgeFilter, parsePlayerRatingFilter} from '@/features/players/playerUrlState';
import {PlayerBrowseState} from '@/features/players/types';

interface PlayersFiltersProps {
  state: PlayerBrowseState;
  searchValue: string;
  facets?: PlayerBrowseFacets;
  showTeam: boolean;
  onChange: (change: Partial<PlayerBrowseState>) => void;
  onSearch: (value: string) => void;
}

const selectClasses = 'input-field min-h-11 min-w-0 max-w-full';
const responsiveFieldClasses = 'w-full min-w-0';

type NumericField = 'minAge' | 'maxAge' | 'minRating' | 'maxRating';
type NumericDrafts = Record<NumericField, string>;
type NumericErrors = Partial<Record<NumericField, string>>;
type NumericDirtyFields = Partial<Record<NumericField, number | null | undefined>>;

const numericFields: NumericField[] = ['minAge', 'maxAge', 'minRating', 'maxRating'];

const draftValue = (value: number | undefined): string => value === undefined ? '' : String(value);

const PlayersFilters: React.FC<PlayersFiltersProps> = ({
  state,
  searchValue,
  facets,
  showTeam,
  onChange,
  onSearch,
}) => {
  const [drafts, setDrafts] = useState<NumericDrafts>(() => ({
    minAge: draftValue(state.minAge),
    maxAge: draftValue(state.maxAge),
    minRating: draftValue(state.minRating),
    maxRating: draftValue(state.maxRating),
  }));
  const [dirtyFields, setDirtyFields] = useState<NumericDirtyFields>({});
  const [focusedField, setFocusedField] = useState<NumericField | null>(null);
  const [errors, setErrors] = useState<NumericErrors>({});

  useEffect(() => {
    const committedValues: Record<NumericField, number | undefined> = {
      minAge: state.minAge,
      maxAge: state.maxAge,
      minRating: state.minRating,
      maxRating: state.maxRating,
    };
    setDrafts(current => ({
      minAge: 'minAge' in dirtyFields || focusedField === 'minAge' ? current.minAge : draftValue(committedValues.minAge),
      maxAge: 'maxAge' in dirtyFields || focusedField === 'maxAge' ? current.maxAge : draftValue(committedValues.maxAge),
      minRating: 'minRating' in dirtyFields || focusedField === 'minRating' ? current.minRating : draftValue(committedValues.minRating),
      maxRating: 'maxRating' in dirtyFields || focusedField === 'maxRating' ? current.maxRating : draftValue(committedValues.maxRating),
    }));
    setDirtyFields(current => {
      const next = {...current};
      let changed = false;
      numericFields.forEach(field => {
        if (field in current && current[field] !== null && current[field] === committedValues[field]) {
          delete next[field];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [dirtyFields, focusedField, state.maxAge, state.maxRating, state.minAge, state.minRating]);

  const resetField = (field: NumericField) => {
    const isAge = field.includes('Age');
    const pair: NumericField[] = isAge ? ['minAge', 'maxAge'] : ['minRating', 'maxRating'];
    const resetDrafts = {...drafts, [field]: draftValue(state[field])};
    const parse = isAge ? parsePlayerAgeFilter : parsePlayerRatingFilter;
    setDrafts(resetDrafts);
    setErrors(current => {
      const next = {...current};
      pair.forEach(pairField => delete next[pairField]);
      const otherField = pair.find(pairField => pairField !== field)!;
      const otherValue = parse(resetDrafts[otherField]);
      if (otherField in dirtyFields && resetDrafts[otherField] !== '' && otherValue === undefined) {
        next[otherField] = 'Enter a number from 0 to 100.';
      } else {
        const min = parse(resetDrafts[pair[0]]);
        const max = parse(resetDrafts[pair[1]]);
        if (min !== undefined && max !== undefined && min > max) {
          const message = `Minimum ${isAge ? 'age' : 'rating'} cannot exceed maximum ${isAge ? 'age' : 'rating'}.`;
          pair.forEach(pairField => { next[pairField] = message; });
        }
      }
      return next;
    });
    setDirtyFields(current => {
      const next = {...current};
      delete next[field];
      return next;
    });
  };

  const commitField = (field: NumericField): boolean => {
    const isAge = field.includes('Age');
    const minField: NumericField = isAge ? 'minAge' : 'minRating';
    const maxField: NumericField = isAge ? 'maxAge' : 'maxRating';
    const parse = isAge ? parsePlayerAgeFilter : parsePlayerRatingFilter;
    const min = parse(drafts[minField]);
    const max = parse(drafts[maxField]);
    const nextErrors: NumericErrors = {...errors};
    delete nextErrors[minField];
    delete nextErrors[maxField];

    [minField, maxField].forEach(pairField => {
      const value = pairField === minField ? min : max;
      if (drafts[pairField] !== '' && value === undefined) {
        nextErrors[pairField] = 'Enter a number from 0 to 100.';
      }
    });
    if (!nextErrors[minField] && !nextErrors[maxField] && min !== undefined && max !== undefined && min > max) {
      const message = `Minimum ${isAge ? 'age' : 'rating'} cannot exceed maximum ${isAge ? 'age' : 'rating'}.`;
      nextErrors[minField] = message;
      nextErrors[maxField] = message;
    }

    setErrors(nextErrors);
    if (nextErrors[minField] || nextErrors[maxField]) return false;

    const parsedValues: Record<NumericField, number | undefined> = {
      minAge: isAge ? min : undefined,
      maxAge: isAge ? max : undefined,
      minRating: isAge ? undefined : min,
      maxRating: isAge ? undefined : max,
    };
    const patch: Partial<PlayerBrowseState> = {};
    [minField, maxField].forEach(pairField => {
      if (pairField in dirtyFields) patch[pairField] = parsedValues[pairField];
    });
    if (Object.keys(patch).length === 0) return true;

    setDirtyFields(current => {
      const next = {...current};
      Object.keys(patch).forEach(patchField => {
        const numericField = patchField as NumericField;
        next[numericField] = parsedValues[numericField];
      });
      return next;
    });
    onChange(patch);
    return true;
  };

  const numericInput = (field: NumericField, label: string) => {
    const inputId = `player-${field.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`;
    return (
      <Input
        id={inputId}
        label={label}
        type="text"
        inputMode="decimal"
        value={drafts[field]}
        error={errors[field]}
        aria-invalid={errors[field] ? true : undefined}
        aria-describedby={errors[field] ? `${inputId}-error` : undefined}
        onFocus={() => setFocusedField(field)}
        onChange={event => {
          setDirtyFields(current => ({...current, [field]: null}));
          setDrafts(current => ({...current, [field]: event.target.value}));
        }}
        onBlur={() => {
          setFocusedField(current => current === field ? null : current);
          commitField(field);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitField(field);
          } else if (event.key === 'Escape') {
            event.preventDefault();
            resetField(field);
          }
        }}
      />
    );
  };

  return (
    <BrowseToolbar
    layout="adaptive-grid"
    search={(
      <Input
        id="player-search"
        type="search"
        label="Search players"
        placeholder="Search by name"
        value={searchValue}
        onChange={event => onSearch(event.target.value)}
      />
    )}
    primaryFilters={(
      <>
        <label className={`${responsiveFieldClasses} min-w-0 text-sm font-medium text-gray-700 dark:text-gray-200`}>
          Position
          <select
            aria-label="Position"
            className={`${selectClasses} mt-1 w-full`}
            value={state.position ?? ''}
            onChange={event => onChange({position: event.target.value as PlayerBrowseState['position'] || undefined})}
          >
            <option value="">All positions</option>
            {(facets?.positions ?? []).map(facet => <option key={facet.value} value={facet.value}>{facet.value} ({facet.count})</option>)}
          </select>
        </label>
        {showTeam && (
          <label className={`${responsiveFieldClasses} min-w-0 text-sm font-medium text-gray-700 dark:text-gray-200`}>
            Team
            <select
              aria-label="Team"
              className={`${selectClasses} mt-1 w-full`}
              value={state.team ?? ''}
              onChange={event => onChange({team: event.target.value || undefined})}
            >
              <option value="">All teams</option>
              {(facets?.teams ?? []).map(facet => <option key={facet.value} value={facet.value}>{facet.value} ({facet.count})</option>)}
            </select>
          </label>
        )}
        <label className={`${responsiveFieldClasses} min-w-0 text-sm font-medium text-gray-700 dark:text-gray-200`}>
          Availability
          <select
            aria-label="Availability"
            className={`${selectClasses} mt-1 w-full`}
            value={state.availability ?? ''}
            onChange={event => onChange({availability: event.target.value as PlayerBrowseState['availability'] || undefined})}
          >
            <option value="">Any availability</option>
            {(facets?.availability ?? []).map(facet => <option key={facet.value} value={facet.value}>{facet.value} ({facet.count})</option>)}
          </select>
        </label>
      </>
    )}
    secondaryFilters={(
      <>
        {numericInput('minAge', 'Minimum age')}
        {numericInput('maxAge', 'Maximum age')}
        {numericInput('minRating', 'Minimum rating')}
        {numericInput('maxRating', 'Maximum rating')}
        <label className="min-w-0 text-sm font-medium text-gray-700 dark:text-gray-200">
          Report recency
          <select className={`${selectClasses} mt-1 w-full`} value={state.recentDays ?? ''} onChange={event => onChange({recentDays: event.target.value ? Number(event.target.value) as PlayerBrowseState['recentDays'] : undefined})}>
            <option value="">Any time</option>
            <option value="7">Last 7 days</option><option value="14">Last 14 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option>
          </select>
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
          <input type="checkbox" checked={state.playedLastMatch} onChange={event => onChange({playedLastMatch: event.target.checked})} />
          Played last match
        </label>
      </>
    )}
    sort={(
      <>
        <label className={`${responsiveFieldClasses} text-sm font-medium text-gray-700 dark:text-gray-200`}>
          Sort by
          <select className={`${selectClasses} mt-1 w-full`} value={state.sort} onChange={event => onChange({sort: event.target.value as PlayerBrowseState['sort']})}>
            <option value="name">Name</option><option value="age">Age</option><option value="position">Position</option><option value="team">Team</option><option value="latest_rating">Latest rating</option><option value="report_recency">Report recency</option>
          </select>
        </label>
        <label className={`${responsiveFieldClasses} text-sm font-medium text-gray-700 dark:text-gray-200`}>
          Order
          <select className={`${selectClasses} mt-1 w-full`} value={state.order} onChange={event => onChange({order: event.target.value as PlayerBrowseState['order']})}>
            <option value="asc">Ascending</option><option value="desc">Descending</option>
          </select>
        </label>
      </>
    )}
    />
  );
};

export default PlayersFilters;
